// ============================================================================
// RankBars — animated horizontal bars for a ranked per-client breakdown
// (busiest clients by sessions, hours, or income).
// ----------------------------------------------------------------------------
// Each bar grows from 0 to its share of the max on scroll-into-view, with a
// gradient fill and a value label. Reduced-motion aware. Horizontal layout
// keeps long client names readable on a 390px phone. The value is rendered by
// a caller-supplied formatter so the same chart serves counts, hours, and
// money in the trainer's currency.
// ============================================================================
import { motion, useReducedMotion } from "framer-motion";
import type { ChartAccent } from "./TrendChart";

const ACCENT_BG: Record<ChartAccent, string> = {
  coral: "bg-grad-coral",
  violet: "bg-grad-violet",
  ocean: "bg-grad-ocean",
  lime: "bg-grad-lime",
};

export interface RankDatum {
  /** Stable key (client id). */
  id: number;
  label: string;
  value: number;
}

export interface RankBarsProps {
  data: RankDatum[];
  accent?: ChartAccent;
  /** Render each bar's value (e.g. "12", "8.5 h", "$420.00"). */
  formatValue: (v: number) => string;
}

/** A list of animated horizontal bars, ranked, sharing one scale. */
export function RankBars({
  data,
  accent = "coral",
  formatValue,
}: RankBarsProps) {
  const reduce = useReducedMotion();
  const max = Math.max(...data.map((d) => d.value), 1);

  return (
    <ul className="space-y-3">
      {data.map((d, i) => {
        const pct = Math.max(6, (d.value / max) * 100);
        return (
          <li key={d.id} className="space-y-1">
            <div className="flex items-baseline justify-between gap-3">
              <span className="truncate font-display text-sm font-bold text-body">
                {d.label}
              </span>
              <span className="nums shrink-0 font-display text-sm font-extrabold tracking-tight text-body">
                {formatValue(d.value)}
              </span>
            </div>
            <div className="h-2.5 w-full overflow-hidden rounded-pill bg-surface-sunken">
              <motion.div
                className={"h-full rounded-pill " + ACCENT_BG[accent]}
                initial={reduce ? { width: `${pct}%` } : { width: 0 }}
                whileInView={{ width: `${pct}%` }}
                viewport={{ once: true, amount: 0.6 }}
                transition={{
                  duration: reduce ? 0 : 0.9,
                  delay: reduce ? 0 : 0.06 * i,
                  ease: [0.22, 0.61, 0.36, 1],
                }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}
